import type { GridApi, IRowNode } from "ag-grid-community";

import { relativeRowToRowId, strictRowStringToCode, type TData } from "@package/grid-utils";

import { splitCellRef } from "../../data/formatting/cell-ref";
import { getRowId } from "../../data/formatting/helpers";

export { periodFromCellRef, splitCellRef } from "../../data/formatting/cell-ref";
export type { SplitCellRef } from "../../data/formatting/cell-ref";

/**
 * A string representation of a reference to a cell in the grid.
 * Uses absolute period (e.g., "Feb26") instead of relative row.
 *
 * @note
 * This is currently used in conditional formatting and alerts when
 * the limit for triggering is based in another cell
 *
 * @example "columnId:productId:fieldSelector:period"
 */
export type CellRef = `${string}:${string}:${string}:${string}`;
export const isCellRef = (cellRef: string): cellRef is CellRef => cellRef.includes(":");

export const parseCellRef = (cellRef: CellRef) => {
  const parsed = splitCellRef(cellRef);
  return {
    columnId: parsed?.columnId ?? "",
    productId: (parsed?.productId || null) as string | null,
    fieldSelector: (parsed?.fieldSelector || null) as string | null,
    period: parsed?.period, // Now using absolute period (may be a strip)
  };
};

export function getRelativeRow(node?: IRowNode<TData>) {
  // Return absolute period directly (not relative)
  const period = node?.data?.rowType === "adhoc" ? node?.data?.id : node?.data?.period;
  return period ?? null;
}

export const cellRef = (p: {
  columnId: string;
  productId?: string | null;
  fieldSelector?: string | null;
  period: string; // Absolute period (e.g., "Feb26")
}): CellRef => `${p.columnId}:${p.productId ?? ""}:${p.fieldSelector ?? ""}:${p.period}`;

/** Resolve a stored period / row id to a live grid row, including REL-* and adhoc formulas. */
export function getRowNodeFromPeriod(grid: GridApi, periodOrRowId: string) {
  const peeled = getRowId(periodOrRowId);
  const resolved = peeled.startsWith("REL-") ? (relativeRowToRowId[peeled] ?? peeled) : peeled;
  const direct = grid.getRowNode(resolved);
  if (direct) return direct;
  // Strip/spread formulas are the row id. Don't coerce them to a month code.
  if (resolved.includes(":") || resolved.includes("/")) return null;
  const rowCode = strictRowStringToCode(resolved);
  if (!rowCode || rowCode === resolved) return null;
  return grid.getRowNode(rowCode) ?? null;
}

export const getCellValueFromRef = (grid: GridApi | null, cellRef: CellRef | string) => {
  if (!isCellRef(cellRef) || !grid) return null;
  const { columnId, period } = parseCellRef(cellRef);
  if (!period) return null;

  const rowNode = getRowNodeFromPeriod(grid, period);
  if (!rowNode) {
    console.error(`Missing row node for period: ${period}`);
    return null;
  }
  return grid.getCellValue({ colKey: columnId, rowNode }) ?? null;
};
